import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { PlusCircleIcon, SparklesIcon, CheckCircleIcon, PlayCircleIcon, ClockIcon, TrophyIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { gameTypes } from '../data/GameTypes';
import { getMetaTags, initializeDynamicSEO } from '../utils/seo';
import PublicGameCreator from '../games/PublicGameCreator';
import { onAuthStateChanged } from 'firebase/auth';

function getGameTypeById(id) {
  return gameTypes.find(gt => gt.id === id);
}

function isNewGame(createdAt) {
  if (!createdAt) return false;
  const now = new Date();
  const created = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  const diff = (now - created) / (1000 * 60 * 60 * 24);
  return diff < 3;
}

function getTimeAgo(createdAt) {
  if (!createdAt) return 'recently';
  const now = new Date();
  const created = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  const diffInMs = now - created;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInMinutes < 1) return 'just now';
  if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays < 7) return `${diffInDays}d ago`;
  return 'recently';
}

// Activity message component with rotating content
const ActivityMessage = () => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  
  const activityMessages = [
    // Quiz/Trivia games
    "Sarah just created 'Marvel Movie Quiz' • 8 people playing now",
    "Book Club finished 'Literary Trivia' • 12 friends sharing results",
    "Office Team playing 'Pop Culture Quiz' right now • 15 coworkers competing",
    
    // List Challenge games  
    "Emma's group just made 'Name All Disney Movies' • 6 people racing to finish",
    "Coffee Crew created 'Top 90s Songs' • 9 friends adding favorites",
    "Study Squad playing 'US Presidents List' • 11 people testing memory",
    
    // Mystery Solver/Hint games
    "Weekend Warriors built 'Guess the Celebrity' • 7 people solving clues",
    "Family Night created 'Mystery Movie Game' • 14 players piecing together hints",
    "Game Night Gang playing 'Famous Landmarks' • 10 friends guessing locations",
    
    // Pin Drop games
    "Brunch Club just made 'Pin World Capitals' • 13 people dropping pins",
    "Travel Squad created 'European Cities' • 8 friends testing geography",
    "History Buffs playing 'Ancient Civilizations' • 16 people pinning sites",
    
    // Snake/Arcade games
    "Pizza Party crew playing 'Snake Challenge' • 5 friends chasing high scores",
    "Lunch Break team just beat 'Cube Dash' • 9 coworkers celebrating"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      // Fade out
      setIsVisible(false);
      
      // After fade out completes, change message and fade back in
      setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % activityMessages.length);
        setIsVisible(true);
      }, 300); // Half second for fade out
      
    }, 8000); // Change every 8 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <span 
      className="transition-opacity duration-300"
      style={{ 
        opacity: isVisible ? 1 : 0,
        minHeight: '1.25rem' // Prevent layout shift during transition
      }}
    >
      {activityMessages[messageIndex]}
    </span>
  );
};

// Game card component to avoid repetition
const GameCard = ({ game }) => {
  const gameType = getGameTypeById(game.gameType);
  const playerCount = Math.floor(Math.random() * 50) + 5; // Simulate player count
  
  return (
    <div
      key={game.id}
      className="group w-full text-left rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 p-4 sm:p-6 flex flex-col focus:outline-none border-2 relative overflow-hidden"
      style={{ 
        backgroundColor: '#34495E',
        borderColor: '#2C3E50',
        minWidth: 0,
        minHeight: '200px' // Ensure consistent card height
      }}
    >
      {/* Mobile overlay for readability */}
      <div className="absolute inset-0 bg-black/30 pointer-events-none rounded-xl" />
      
      {/* Header with icon and game type */}
      <div className="flex items-start gap-3 mb-3 relative z-10">
        <div className="flex items-center justify-center w-14 h-14 sm:w-12 sm:h-12 rounded-lg border-2 flex-shrink-0" style={{
          backgroundColor: '#2C3E50',
          borderColor: '#F1C40F'
        }}>
          {gameType?.icon && gameType.icon({ className: 'w-7 h-7 sm:w-6 sm:h-6', style: { color: '#F1C40F' } })}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-lg font-bold line-clamp-2 break-words leading-tight tracking-wide mb-1" style={{ color: '#F1C40F' }}>
                {game.options?.title || 'Untitled Game'}
              </h3>
              <div className="flex items-center gap-2 text-xs opacity-75" style={{ color: '#ECF0F1' }}>
                <span>{playerCount} played</span>
                {isNewGame(game.createdAt) && (
                  <>
                    <span>•</span>
                    <span className="px-2 py-0.5 text-xs font-bold rounded-full border" style={{
                      backgroundColor: '#2ECC71',
                      color: '#FFFFFF',
                      borderColor: '#27AE60'
                    }}>
                      HOT
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Description */}
      <p className="text-sm sm:text-xs line-clamp-3 sm:line-clamp-2 mb-4 font-bold flex-1 relative z-10" style={{ color: '#ECF0F1' }}>
        {game.options?.intro || gameType?.description || 'A fun game that will test what you really know about your friends!'}
      </p>
      
      {/* Play button with better mobile sizing */}
      <a
        href={`${window.location.origin}/play/${game.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full rounded-lg py-3 sm:py-2 text-center font-bold text-white shadow-lg hover:scale-105 transition-all duration-150 flex items-center justify-center gap-2 text-base sm:text-sm border-2 relative z-10"
        style={{
          backgroundColor: '#E74C3C',
          borderColor: '#C0392B'
        }}
      >
        <PlayCircleIcon className="w-6 h-6 sm:w-5 sm:h-5" /> 
        Play Now ({playerCount} played)
      </a>
    </div>
  );
};

const ArcadeLanding = () => {
  const [featuredGames, setFeaturedGames] = useState([]);
  const [latestGames, setLatestGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fontLoaded, setFontLoaded] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const showcaseRef = useRef(null);
  
  // Hardcode brandName and createPath
  const brandName = 'Group Chat Heroes';
  const createPath = '/create';
  const meta = getMetaTags();

  useEffect(() => {
    // Initialize dynamic SEO (favicon, title)
    initializeDynamicSEO();
    
    // Load Share Tech Mono font (retro style)
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Share+Tech+Mono:wght@400&display=swap';
    link.rel = 'stylesheet';
    link.rel = 'preload';
    link.as = 'style';
    link.onload = function() { this.rel = 'stylesheet'; };
    document.head.appendChild(link);
    
    setFontLoaded(true); // Set immediately to prevent flash
    
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
      unsubscribe();
    };
  }, []);

  const retroStyle = {
    fontFamily: '"Share Tech Mono", monospace',
    transition: 'all 0.3s ease-in-out'
  };

  useEffect(() => {
    // Cache games for 2 minutes to reduce DB reads
    const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes
    const cacheKey = 'arcade_games_cache';
    
    const fetchGames = async () => {
      setLoading(true);
      
      try {
        // Check cache first
        const cached = sessionStorage.getItem(cacheKey);
        const cacheTime = sessionStorage.getItem(`${cacheKey}_time`);
        
        if (cached && cacheTime && (Date.now() - parseInt(cacheTime)) < CACHE_DURATION) {
          const { featured, latest } = JSON.parse(cached);
          setFeaturedGames(featured);
          setLatestGames(latest);
          setLoading(false);
          return;
        }

        // Single optimized query - get all recent games at once
        const gamesQuery = query(
          collection(db, 'campaign'),
          where('arcade', '==', true),
          where('private', '==', false),
          where('status', '==', 'live'),
          orderBy('createdAt', 'desc'),
          limit(20) // Get enough to populate both sections
        );
        
        const snapshot = await getDocs(gamesQuery);
        const allGames = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Separate featured and latest games
        const featured = allGames.filter(game => game.featured === true).slice(0, 6);
        const latest = allGames.filter(game => game.featured !== true).slice(0, 6);
        
        setFeaturedGames(featured);
        setLatestGames(latest);
        
        // Cache the results
        sessionStorage.setItem(cacheKey, JSON.stringify({ featured, latest }));
        sessionStorage.setItem(`${cacheKey}_time`, Date.now().toString());
        
      } catch (error) {
        console.error('Error fetching arcade games:', error);
        // On error, try to use any cached data as fallback
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const { featured, latest } = JSON.parse(cached);
          setFeaturedGames(featured);
          setLatestGames(latest);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchGames();
  }, []);

  return (
    <>
      <style jsx>{`
        .hero-title {
          text-shadow: 2px 2px 0px #E67E22, 4px 4px 0px #D35400, 2px 2px 10px rgba(241, 196, 15, 0.5);
          -webkit-text-stroke: 1px #E67E22;
        }
        
        .feature-card {
          backdrop-filter: blur(10px);
          border: 1px solid rgba(241, 196, 15, 0.2);
          background: rgba(52, 73, 94, 0.3);
        }
        
        .retro-button {
          box-shadow: 0 6px 0 #C0392B, 0 8px 15px rgba(231, 76, 60, 0.3);
          transform: translateY(0);
          transition: all 0.1s ease;
        }
        .retro-button:hover {
          transform: translateY(2px);
          box-shadow: 0 4px 0 #C0392B, 0 6px 12px rgba(231, 76, 60, 0.3);
        }
        .retro-button:active {
          transform: translateY(6px);
          box-shadow: 0 0px 0 #C0392B, 0 2px 8px rgba(231, 76, 60, 0.3);
        }
        
        @media (min-width: 640px) {
          .hero-title {
            text-shadow: 2px 2px 0px #E67E22, 4px 4px 0px #D35400, 2px 2px 10px rgba(241, 196, 15, 0.5);
            -webkit-text-stroke: 1px #E67E22;
          }
        }
      `}</style>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={meta.keywords} />
        <meta name="theme-color" content={meta.themeColor} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.domain} />
        <meta property="og:site_name" content={meta.siteName} />
        <meta property="og:image" content={meta.image} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
        {meta.twitterSite && <meta name="twitter:site" content={meta.twitterSite} />}
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content={meta.author} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href={meta.domain} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Group Chat Heroes Game Creator",
            "description": meta.description,
            "url": meta.domain,
            "applicationCategory": "GameApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "creator": {
              "@type": "Organization",
              "name": meta.siteName,
              "url": meta.domain
            },
            "featureList": [
              "Quiz Game Creator",
              "Snake Game",
              "Geography Games",
              "List Games",
              "Live Leaderboards",
              "Mobile-Friendly",
              "No Downloads Required"
            ]
          })}
        </script>
      </Helmet>
            
      <div className="min-h-screen" style={{
        ...retroStyle,
        background: 'linear-gradient(to bottom, #2C3E50, #1A2530)'
      }}>
                {/* Hero Section - Always visible */}
        <header className="relative py-20 px-4 text-center overflow-hidden flex flex-col items-center justify-center min-h-[500px] pt-24 sm:pt-20">
          {/* Top-right navigation button */}
          <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20">
            {user ? (
              <button
                onClick={() => navigate('/studio/dashboard')}
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-lg border-2 transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: '#34495E',
                  borderColor: '#F1C40F',
                  color: '#F1C40F'
                }}
              >
                DASHBOARD
              </button>
            ) : (
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => navigate('/studio/login')}
                  className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-lg border-2 transition-all duration-200 hover:scale-105"
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: '#ECF0F1',
                    color: '#ECF0F1'
                  }}
                >
                  SIGN IN
                </button>
                <button
                  onClick={() => navigate('/studio/register')}
                  className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-lg border-2 transition-all duration-200 hover:scale-105"
                  style={{
                    backgroundColor: '#E74C3C',
                    borderColor: '#C0392B',
                    color: '#FFFFFF'
                  }}
                >
                  JOIN STUDIO
                </button>
              </div>
            )}
          </div>

          {/* Starfield background effect */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Animated stars */}
            <div className="absolute top-10 left-10 w-1 h-1 bg-yellow-400 animate-pulse"></div>
            <div className="absolute top-20 right-20 w-1 h-1 bg-yellow-400 animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-yellow-400 animate-pulse" style={{ animationDelay: '2s' }}></div>
            <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-yellow-400 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute bottom-20 right-10 w-1 h-1 bg-yellow-400 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
          </div>

          {/* Gaming elements scattered around */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Top left area */}
            <div className="absolute top-8 left-8 text-orange-400 font-bold text-xl animate-bounce" style={{ animationDelay: '2s', animationDuration: '3s' }}>
              1UP
            </div>
            
            {/* Gaming controllers and elements */}
            <div className="absolute top-16 right-12 text-3xl animate-pulse" style={{ animationDelay: '1s' }}>🎮</div>
            <div className="absolute top-32 left-16 text-2xl animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '2s' }}>⭐</div>
            <div className="absolute bottom-40 right-20 text-2xl animate-pulse" style={{ animationDelay: '1.5s' }}>🎲</div>
            <div className="absolute top-1/3 left-8 text-xl animate-bounce" style={{ animationDelay: '2.5s', animationDuration: '2.5s' }}>✨</div>
            <div className="absolute bottom-32 left-12 text-2xl animate-pulse" style={{ animationDelay: '0.8s' }}>🚀</div>
            <div className="absolute top-20 left-1/3 text-orange-400 text-lg animate-pulse" style={{ animationDelay: '3s' }}>+</div>
            <div className="absolute bottom-48 right-1/4 text-orange-400 text-lg animate-pulse" style={{ animationDelay: '2.2s' }}>+</div>
            <div className="absolute top-40 right-1/3 text-purple-400 text-xl animate-bounce" style={{ animationDelay: '1.8s', animationDuration: '2.8s' }}>⚡</div>
          </div>

          {/* Main title with retro styling */}
          <div className="relative z-10 mb-12">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-8 tracking-wider leading-none hero-title" 
                style={{ 
                  color: '#F1C40F',
                  fontFamily: '"Share Tech Mono", monospace'
                }}>
              <div>GROUP</div>
              <div>CHAT</div>
              <div>HEROES</div>
            </h1>
            
            {/* Enhanced subtitle with better messaging */}
            <div className="relative inline-block mb-6">
              <div className="px-4 sm:px-8 py-3 sm:py-4 rounded-lg border-2 sm:border-4 border-yellow-400 bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 shadow-lg"
                   style={{
                     background: 'linear-gradient(45deg, #8E44AD, #9B59B6, #8E44AD)',
                   }}>
                <p className="text-sm sm:text-xl lg:text-2xl font-black tracking-wide sm:tracking-widest" 
                   style={{ 
                     color: '#F1C40F',
                     textShadow: '1px 1px 0px #E67E22, -1px -1px 0px #E67E22'
                   }}>
                  TURN ANY TOPIC INTO A GAME
                </p>
              </div>
            </div>



            {/* Social proof indicator */}
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-green-400 bg-green-900/30 backdrop-blur-sm">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-bold text-green-300">
                  <ActivityMessage />
                </span>
              </div>
            </div>
          </div>

          {/* Action button */}
          <div className="relative z-10">
            {showCreator ? (
              <button
                onClick={() => setShowCreator(false)}
                className="inline-flex items-center gap-3 px-8 py-4 text-white rounded-xl shadow-lg font-bold text-lg transition-transform duration-300 ease-out border-4 hover:scale-105 sm:hover:rotate-1 active:scale-95"
                style={{
                  backgroundColor: '#34495E',
                  borderColor: '#2C3E50',
                  boxShadow: '0 8px 0 #2C3E50, 0 12px 20px rgba(0,0,0,0.3)',
                  willChange: 'transform'
                }}
              >
                <XMarkIcon className="w-6 h-6" />
                CLOSE
              </button>
            ) : (
              <button
                onClick={() => setShowCreator(true)}
                className="inline-flex items-center gap-3 px-10 py-5 text-white rounded-xl shadow-lg font-bold text-xl transition-transform duration-300 ease-out border-4 hover:scale-105 sm:hover:-rotate-1 active:scale-95"
                style={{
                  backgroundColor: '#E74C3C',
                  borderColor: '#C0392B',
                  boxShadow: '0 8px 0 #C0392B, 0 12px 20px rgba(231, 76, 60, 0.4)',
                  textShadow: '2px 2px 0px #C0392B',
                  willChange: 'transform'
                }}
              >
                <PlusCircleIcon className="w-7 h-7" />
                MAKE YOUR GAME
              </button>
            )}
            
            <p className="mt-4 text-sm font-bold opacity-80" style={{ color: '#ECF0F1' }}>
              No downloads • No sign-ups • Works on any phone
            </p>
          </div>
        </header>

        {showCreator && (
          // Game Creator Component
          <div className="max-w-6xl mx-auto mb-16">
            <PublicGameCreator />
          </div>
        )}



        {/* Landing Page Content - Hidden when creator is showing */}
        {!showCreator && (loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="text-center">
              <div className="relative mb-4">
                <div className="w-12 h-12 border-4 rounded-full animate-spin" style={{
                  borderColor: '#2C3E50',
                  borderTopColor: '#F1C40F'
                }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg">🎮</span>
                </div>
              </div>
              <p className="text-sm font-bold font-mono" style={{ 
                color: '#F1C40F',
                fontFamily: "'Share Tech Mono', monospace"
              }}>
                LOADING GAMES...
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Featured Games Section */}
            {featuredGames.length > 0 && (
              <section className="max-w-5xl mx-auto px-4 py-12">
                <div className="text-center mb-8">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-center flex items-center justify-center gap-2 tracking-wider" style={{ color: '#F1C40F' }}>
                  
                    Featured Games
                  </h2>
                  <p className="text-sm font-bold opacity-80" style={{ color: '#ECF0F1' }}>
                    Real games created by people like you
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {featuredGames.slice(0, 4).map(game => (
                    <GameCard key={game.id} game={game} />
                  ))}
                </div>
                {featuredGames.length > 4 && (
                  <div className="text-center mt-6">
                    <button
                      onClick={() => {
                        // Show all games or scroll to see more
                        const section = document.querySelector('#all-games');
                        if (section) section.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="px-6 py-2 rounded-lg font-bold border-2 transition-all duration-200 hover:scale-105"
                      style={{
                        backgroundColor: 'transparent',
                        borderColor: '#F1C40F',
                        color: '#F1C40F'
                      }}
                    >
                      See All Games ({featuredGames.length})
                    </button>
                  </div>
                )}
              </section>
            )}

            {/* No games fallback */}
            {featuredGames.length === 0 && latestGames.length === 0 && (
              <section className="max-w-5xl mx-auto px-4 py-12">
                <div className="text-center py-16 flex flex-col items-center" style={{ color: '#ECF0F1' }}>
                  <span className="text-5xl mb-2">🎲</span>
                  <div className="font-bold">No games yet. Be the first to <button className="underline" style={{ color: '#F1C40F' }} onClick={() => setShowCreator(true)}>create one</button>!</div>
                </div>
              </section>
            )}

            {/* Recent Activity Section */}
            <section className="max-w-4xl mx-auto px-4 py-12">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4 tracking-wider" style={{ color: '#F1C40F' }}>
                  🔥 What's Happening Now
                </h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {latestGames.slice(0, 9).map((game, index) => {
                    const gameType = getGameTypeById(game.gameType);
                    const timeAgo = getTimeAgo(game.createdAt);
                    const playerCount = Math.floor(Math.random() * 20) + 5; // Simulate recent players
                    const creatorName = game.options?.creatorName || 'Someone';
                    
                    return (
                      <a
                        key={game.id}
                        href={`/play/${game.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-4 rounded-lg border border-opacity-30 backdrop-blur-sm hover:scale-105 transition-transform duration-200 cursor-pointer block"
                        style={{
                          backgroundColor: 'rgba(52, 73, 94, 0.3)',
                          borderColor: '#F1C40F'
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {gameType?.icon && gameType.icon({ className: 'w-4 h-4', style: { color: '#F1C40F' } })}
                            <span className="font-bold text-sm" style={{ color: '#F1C40F' }}>
                              Created for {creatorName}
                            </span>
                          </div>
                          <span className="text-xs opacity-75" style={{ color: '#ECF0F1' }}>
                            {timeAgo}
                          </span>
                        </div>
                                <p className="text-sm font-bold mb-2 line-clamp-1" style={{ color: '#ECF0F1' }}>
          "{game.options?.title || 'Untitled Game'}"
        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-xs" style={{ color: '#2ECC71' }}>
                            <span>•</span>
                            <span>{playerCount} played recently</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {timeAgo.includes('min') && parseInt(timeAgo) < 5 && (
                              <span className="text-xs px-2 py-0.5 rounded-full" style={{ 
                                backgroundColor: '#E74C3C', 
                                color: '#FFFFFF' 
                              }}>
                                HOT
                              </span>
                            )}
                            <span className="text-xs px-2 py-1 rounded" style={{ 
                              backgroundColor: '#F1C40F', 
                              color: '#2C3E50',
                              fontWeight: 'bold'
                            }}>
                              PLAY →
                            </span>
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
            </section>
          </>
        ))}

        {/* How it works - Enhanced version */}
        <section className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black mb-4 tracking-wider hero-title" style={{ 
              color: '#F1C40F',
              fontFamily: '"Share Tech Mono", monospace'
            }}>
              BECOME THE GROUP CHAT LEGEND
            </h2>
            <p className="text-xl font-bold max-w-3xl mx-auto mb-6" style={{ color: '#ECF0F1' }}>
              In 60 seconds, you'll go from "what should we do?" to "OMG this is amazing!" 🤯
            </p>
            
            {/* Social proof mini-banner */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-green-400 bg-green-900/20 mb-8">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-bold text-green-300">
                347 GROUPS BECAME LEGENDS THIS WEEK
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Step 1 - Enhanced */}
            <div className="text-center group relative">
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-black border-2" style={{
                backgroundColor: '#E74C3C',
                borderColor: '#F1C40F',
                color: '#FFFFFF'
              }}>
                30s
              </div>
              <div className="flex items-center justify-center w-20 h-20 rounded-full mx-auto mb-4 border-4 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" style={{
                backgroundColor: '#E74C3C',
                borderColor: '#F1C40F'
              }}>
                <span className="text-3xl">🎯</span>
              </div>
              <h3 className="text-xl font-black mb-3 tracking-wide" style={{ 
                color: '#F1C40F',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                PICK YOUR WEAPON
              </h3>
              <p className="font-bold text-sm leading-relaxed" style={{ color: '#ECF0F1' }}>
                Marvel trivia? Friends IQ test? Pin drop challenge? Snake high scores? 
                <br />
                <span style={{ color: '#F39C12' }}>Choose your battlefield!</span>
              </p>
            </div>

            {/* Step 2 - Enhanced */}
            <div className="text-center group relative">
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-black border-2" style={{
                backgroundColor: '#2ECC71',
                borderColor: '#F1C40F',
                color: '#FFFFFF'
              }}>
                10s
              </div>
              <div className="flex items-center justify-center w-20 h-20 rounded-full mx-auto mb-4 border-4 group-hover:scale-110 group-hover:-rotate-12 transition-all duration-300" style={{
                backgroundColor: '#2ECC71',
                borderColor: '#F1C40F'
              }}>
                <span className="text-3xl">💣</span>
              </div>
              <h3 className="text-xl font-black mb-3 tracking-wide" style={{ 
                color: '#F1C40F',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                DROP THE BOMB
              </h3>
              <p className="font-bold text-sm leading-relaxed" style={{ color: '#ECF0F1' }}>
                Copy link → paste in group chat → watch chaos unfold.
                <br />
                <span style={{ color: '#F39C12' }}>No apps, no signups, just pure genius!</span>
              </p>
            </div>

            {/* Step 3 - Enhanced */}
            <div className="text-center group relative">
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-black border-2" style={{
                backgroundColor: '#F39C12',
                borderColor: '#F1C40F',
                color: '#FFFFFF'
              }}>
                ∞
              </div>
              <div className="flex items-center justify-center w-20 h-20 rounded-full mx-auto mb-4 border-4 group-hover:scale-110 group-hover:rotate-180 transition-all duration-500" style={{
                backgroundColor: '#F39C12',
                borderColor: '#F1C40F'
              }}>
                <span className="text-3xl">👑</span>
              </div>
                             <h3 className="text-xl font-black mb-3 tracking-wide" style={{ 
                 color: '#F1C40F',
                 fontFamily: '"Share Tech Mono", monospace'
               }}>
                CLAIM YOUR THRONE
              </h3>
              <p className="font-bold text-sm leading-relaxed" style={{ color: '#ECF0F1' }}>
                Live leaderboard = instant bragging rights.
                <br />
                <span style={{ color: '#F39C12' }}>Finally prove you're the smartest! 🧠</span>
              </p>
            </div>
          </div>

          {/* Enhanced bottom section with urgency */}
          <div className="text-center">
            <div className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl border-4 mb-6" style={{
              backgroundColor: 'rgba(231, 76, 60, 0.2)',
              borderColor: '#E74C3C',
              background: 'linear-gradient(135deg, rgba(231, 76, 60, 0.2), rgba(192, 57, 43, 0.3))'
            }}>
              <span className="text-2xl animate-pulse">⚡</span>
              <div className="text-left">
                <div className="font-black text-lg tracking-wide" style={{ color: '#F1C40F' }}>
                  DEAD SIMPLE • ACTUALLY ADDICTIVE
                </div>
                <div className="font-bold text-sm" style={{ color: '#ECF0F1' }}>
                  Works on literally every phone • No downloads ever • Your friends will thank you
                </div>
              </div>
            </div>
            
            
          </div>
        </section>

        {/* Group Chat Heroes Studio Section - conversion section */}
        <section className="max-w-4xl mx-auto px-4 py-16">
          <div className="rounded-2xl p-8 sm:p-12 text-center shadow-2xl border-2" style={{
            backgroundColor: '#34495E',
            borderColor: '#F1C40F',
            background: 'linear-gradient(135deg, #34495E, #2C3E50)'
          }}>
            {/* Header Section */}
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 border-4" style={{
                backgroundColor: '#E74C3C',
                borderColor: '#F1C40F'
              }}>
                <span className="text-3xl">🚀</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-black mb-4 tracking-wider" style={{
                color: '#F1C40F',
                fontFamily: '"Share Tech Mono", monospace',
                textShadow: '2px 2px 0px #B7950B'
              }}>
                READY FOR BUSINESS?
              </h2>
              
              <h3 className="text-xl sm:text-2xl font-bold mb-6 tracking-wide" style={{ 
                color: '#ECF0F1',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                TURN CONTENT INTO CUSTOMERS
              </h3>
            </div>

            {/* Description */}
            <p className="mb-8 font-bold text-lg leading-relaxed max-w-3xl mx-auto" style={{ color: '#ECF0F1' }}>
              Create branded experiences that turn visitors into paying customers. Perfect for businesses, agencies, and marketing professionals looking to engage their audience in new ways.
            </p>
            
            {/* Features Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-10">
              <div className="flex flex-col items-center text-center p-4 rounded-lg border-2" style={{
                backgroundColor: 'rgba(52, 73, 94, 0.3)',
                borderColor: '#2ECC71'
              }}>
                <CheckCircleIcon className="w-8 h-8 mb-3" style={{ color: '#2ECC71' }} />
                <span className="font-bold text-sm" style={{ color: '#ECF0F1' }}>Your brand everywhere</span>
              </div>
              <div className="flex flex-col items-center text-center p-4 rounded-lg border-2" style={{
                backgroundColor: 'rgba(52, 73, 94, 0.3)',
                borderColor: '#2ECC71'
              }}>
                <CheckCircleIcon className="w-8 h-8 mb-3" style={{ color: '#2ECC71' }} />
                <span className="font-bold text-sm" style={{ color: '#ECF0F1' }}>Track every player</span>
              </div>
              <div className="flex flex-col items-center text-center p-4 rounded-lg border-2" style={{
                backgroundColor: 'rgba(52, 73, 94, 0.3)',
                borderColor: '#2ECC71'
              }}>
                <CheckCircleIcon className="w-8 h-8 mb-3" style={{ color: '#2ECC71' }} />
                <span className="font-bold text-sm" style={{ color: '#ECF0F1' }}>Capture warm leads</span>
              </div>
              <div className="flex flex-col items-center text-center p-4 rounded-lg border-2" style={{
                backgroundColor: 'rgba(52, 73, 94, 0.3)',
                borderColor: '#2ECC71'
              }}>
                <CheckCircleIcon className="w-8 h-8 mb-3" style={{ color: '#2ECC71' }} />
                <span className="font-bold text-sm" style={{ color: '#ECF0F1' }}>Share anywhere</span>
              </div>
            </div>
            
            {/* CTA Button */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => navigate('/studio/register')}
                className="inline-flex items-center gap-3 px-12 py-4 text-white rounded-xl font-bold shadow-lg hover:scale-105 transition-all duration-200 text-xl border-4 mb-4"
                style={{
                  backgroundColor: '#E74C3C',
                  borderColor: '#C0392B',
                  boxShadow: '0 8px 0 #C0392B, 0 12px 20px rgba(231, 76, 60, 0.4)',
                  fontFamily: '"Share Tech Mono", monospace',
                  textShadow: '2px 2px 0px #C0392B'
                }}
              >
                🎯 START 7-DAY FREE TRIAL
              </button>
              
              <div className="text-sm font-bold tracking-wider" style={{ 
                color: '#F1C40F',
                fontFamily: '"Share Tech Mono", monospace'
              }}>
                TRUSTED BY AGENCIES & MARKETING TEAMS
              </div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-4xl mx-auto my-8" style={{
          borderTop: '2px solid #F1C40F'
        }} />

        {/* Footer */}
        <footer className="py-8 text-center text-sm font-bold" style={{ color: '#ECF0F1' }}>
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mb-6">
            <a 
              href="/privacy" 
              className="hover:opacity-80 transition-opacity border-b border-transparent hover:border-current"
              style={{ color: '#F1C40F' }}
            >
              Privacy Policy
            </a>
            <a 
              href="/terms" 
              className="hover:opacity-80 transition-opacity border-b border-transparent hover:border-current"
              style={{ color: '#F1C40F' }}
            >
              Terms of Service
            </a>
            <a 
              href="/support" 
              className="hover:opacity-80 transition-opacity border-b border-transparent hover:border-current"
              style={{ color: '#F1C40F' }}
            >
              Support
            </a>
          </div>
          
          {/* Main Footer Message */}
          <div>
            Made with <span style={{ color: '#E74C3C' }}>❤️</span> for friends everywhere.<br />
            <span className="text-xs" style={{ opacity: 0.8 }}>Powered by Group Chat Heroes</span>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ArcadeLanding; 